import {
  AdContainerType,
  AdEventDataType,
  AdEvents,
  AdsApi,
  AnalyticsEventParam,
  AnalyticsTechSetAdContextsEvent,
  LoadProps,
} from '@accedo/one-plugin-sdk';
import { applyDatabinding } from '@dynamic/ui';
import { RefObject } from 'react';
import { proxy, ref, useSnapshot } from 'valtio';

import { Item } from '#/client';
import devtoolsApi from '#/dev/devtools';
import { FeatureAdsProps } from '#/service/config';
import { createItemPayload } from '#/service/features/analytics/utils';
import { authStore } from '#/store/auth';

export type ExtendedAdType = {
  apiRef: RefObject<AdsApi>;
  properties: FeatureAdsProps;
};
export interface AdsStore {
  adWrapper: HTMLElement | null;
  ads: ExtendedAdType[];
  isAdLoading: boolean;
  isAdPlaying: boolean;
  isInsideAd: boolean;
  isLoaded: boolean;
  markers: AdEventDataType['cuepoints'];
  videoElement: HTMLMediaElement | null;
  contentPausedOn: number | null;
}

interface ExtraLoadProps {
  resource?: Item;
}

type EventListenerType = {
  event: string;
  callback: (data?: any) => void;
};

export const adsStore = proxy<AdsStore>({
  adWrapper: null,
  ads: [],
  markers: [],
  isAdLoading: false,
  isAdPlaying: false,
  isInsideAd: false,
  isLoaded: false,
  videoElement: null,
  contentPausedOn: null,
});

const eventListeners: EventListenerType[] = [];
const eventListenersQueue: EventListenerType[] = [];

export const updateIsInsideAd =
  (isAdBreakStarted: boolean) =>
  ({ contentPausedOn }: AdEventDataType) => {
    adsActions.setIsInsideAd(isAdBreakStarted);

    if (isAdBreakStarted) {
      // Store content paused on time when starting an ad so we can
      // keep track of which markers should we remove
      adsStore.contentPausedOn = contentPausedOn || null;
    } else {
      // Force terminating all loading states if ad break is finished
      adsActions.setIsAdLoading(false);
      adsActions.setIsAdPlaying(false);
    }
  };

const updateIsAdPlaying = (value: boolean) => () =>
  adsActions.setIsAdPlaying(value);

export const adsActions = {
  addAds(ad: ExtendedAdType) {
    adsStore.ads.push(ad);
  },
  getAdContexts() {
    return adsStore.ads.map((ad) => ad.apiRef.current?.getStreamManager());
  },
  addEventListener(event: string, callback: (data?: any) => void) {
    if (adsStore.isLoaded) {
      adsStore.ads.forEach((ad) => {
        if (
          !eventListeners.find(
            (eventListener) => eventListener.callback === callback
          )
        ) {
          eventListeners.push({ event, callback });
          ad.apiRef.current?.addEventListener(event, callback);
        }
      });
    } else {
      eventListenersQueue.push({ event, callback });
    }
  },
  removeEventListener(event: string, callback: (data?: any) => void) {
    const eventListenerIndex = eventListeners.findIndex(
      (eventListener) => eventListener.callback === callback
    );

    if (eventListenerIndex > -1) {
      eventListeners.splice(eventListenerIndex, 1);
      adsStore.ads.forEach((ad) => {
        ad.apiRef.current?.removeEventListener(event, callback);
      });
    } else {
      // If the handler is being removed before the ad plugin is loaded the eventlistener only exists in the queue
      // so we need to remove from it before it will load the queue.
      const eventListenerIndexFromQueue = eventListenersQueue.findIndex(
        (eventListener) => eventListener.callback === callback
      );
      eventListenersQueue.splice(eventListenerIndexFromQueue, 1);
    }
  },
  play() {
    adsStore.ads.forEach((ad) => {
      ad.apiRef.current?.play();
    });
  },
  pause() {
    adsStore.ads.forEach((ad) => {
      ad.apiRef.current?.pause();
    });
  },
  skip() {
    adsStore.ads.forEach((ad) => {
      ad.apiRef.current?.skip();
    });
  },
  stop() {
    adsStore.ads.forEach((ad) => {
      ad.apiRef.current?.stop();
    });
  },
  assignVideoPlayer(videoElement: AdsStore['videoElement']) {
    if (videoElement && videoElement !== adsStore.videoElement) {
      adsStore.videoElement = ref(videoElement);
    }
  },
  assignAdWrapper(adWrapper: AdsStore['adWrapper']) {
    if (adWrapper) {
      adsStore.adWrapper = ref(adWrapper);
    }
  },
  isLoadingOrPlayingAd() {
    return adsStore.isAdPlaying || adsStore.isAdLoading;
  },
  /**
   * Method used for calculating the content time when provided the absolute time and an array of cuepoints.  It removes the additional time of markers that have passed.
   * @param absoluteTime full length of content on video element, for example ssai stream with ads stitched into content and the original content
   * @param alternativeMarkers optional markers if the function needs to consider additional markers, i.e. if this method needs to be called before initialization of adsStore.
   * @returns Calculated content time.
   */
  getContentTime(
    absoluteTime?: number,
    alternativeMarkers?: AdEventDataType['cuepoints']
  ) {
    const markers = alternativeMarkers ?? adsStore.markers ?? [];
    const hasMarkers = !!markers.length;

    if (!hasMarkers || !absoluteTime) {
      return absoluteTime ?? 0;
    }

    let totalElapsedTime = 0;
    markers.forEach(({ start, end }) => {
      if (absoluteTime > start) {
        totalElapsedTime += end - start;
      }
    });

    return Math.max(absoluteTime - totalElapsedTime, 0);
  },
  /**
   * Method used for calculating the total duration of content time minus advertisement time.  When provided the absolute time and an array of cuepoints.  It removes the additional time of markers.
   * @param absoluteDuration full length of content on video element, for example ssai stream with ads stitched into content and the original content
   * @param alternativeMarkers optional markers if the function needs to consider additional markers, i.e. if this method needs to be called before initialization of adsStore.
   * @returns Calculated content duration.
   */
  getContentDuration: (
    absoluteDuration?: number,
    alternativeMarkers?: AdEventDataType['cuepoints']
  ) => {
    const markers = alternativeMarkers ?? adsStore.markers ?? [];
    const hasMarkers = !!markers.length;

    if (!hasMarkers) {
      return absoluteDuration ?? 0;
    }

    const totalAdTime =
      markers
        ?.map(({ start, end }) => end - start)
        .reduce((partialSum, a) => partialSum + a, 0) || 0;

    const contentDuration = (absoluteDuration ?? 0) - totalAdTime;

    return Math.max(contentDuration, 0);
  },
  /**
   * Method used for calculating the total duration of content time minus advertisement time.  When provided the content time (see method above) and an array of cuepoints.  It removes the additional time of markers.
   * @param contentTime length of content time on video element, without ad time included.
   * @param alternativeMarkers optional markers if the function needs to consider additional markers, i.e. if this method needs to be called before initialization of adsStore.
   * @returns Calculated absolute time.
   */
  getAbsoluteTime: (
    contentTime?: number,
    alternativeMarkers?: AdEventDataType['cuepoints']
  ) => {
    const markers = alternativeMarkers ?? adsStore.markers ?? [];
    const hasMarkers = !!markers.length;

    if (!hasMarkers || !contentTime) {
      return contentTime ?? 0;
    }

    let absoluteTime = contentTime;

    markers.forEach(({ start, end }) => {
      if (absoluteTime > start) {
        absoluteTime += end - start;
      }
    });

    return absoluteTime;
  },
  setIsInsideAd(value: boolean) {
    adsStore.isAdPlaying = value;
    adsStore.isInsideAd = value;
  },
  setIsAdPlaying(value: boolean) {
    adsStore.isAdPlaying = value;
  },
  setIsAdLoading(value: boolean) {
    adsStore.isAdLoading = value;
  },
  removeMarkersUntil(removeUntil: number) {
    adsStore.markers = adsStore.markers.filter(
      (marker) => marker.start > removeUntil
    );
  },
  includesPostroll() {
    return adsStore.ads.some((ad) => ad.apiRef.current?.includesPostroll());
  },
  async loadAds(props: LoadProps & ExtraLoadProps) {
    const loadPromises: Promise<unknown>[] = [];

    adsStore.ads.forEach((ad) => {
      if (ad.apiRef.current) {
        const keyValues: FeatureAdsProps['keyValues'] = {};

        if (ad.properties.keyValues) {
          Object.entries(ad.properties.keyValues).forEach(([key, value]) => {
            keyValues[key] = applyDatabinding({
              value,
              context: {
                item: props.resource,
                videoElement: props.videoElement,
                entitlements: authStore.entitlements,
              },
            });
          });
        }
        const adContainer = (
          ad.apiRef.current.getAdContainerType?.() === AdContainerType.Parent
            ? adsStore.adWrapper
            : props.adContainer
        ) as HTMLElement | undefined;
        loadPromises.push(
          ad.apiRef.current.load({
            ...props,
            adContext: {
              platform: {
                userAgent: navigator.userAgent,
                hostname: window.location.hostname,
                currentUrl: window.location.href,
              },
              entitlements: authStore.entitlements,
            },
            databinder: applyDatabinding,
            properties: ad.properties,
            adContainer,
            keyValues,
          })
        );

        // If an adcontainer is required we are using the same video instance for content and ads.
        // It means that we always have to load the ad media to the player to avoid unexpected errors
        // because some ad providers like FreeWheel just replaces the src and starts to play immediately.
        // The workaround is to introduce a variable which indicates whether an ad is being loaded into the player
        // and it's value is set to true when an ad break starts and when an individual ad is completed.
        // Setting this value to false is the responsibility of each players because they have different logic
        // and events to listen on to determine when the actual media is fully loaded and ready to be played.
        // TODO#ads: Currently only Akamai player supports setting this value to false, other players should be prepared too.
        if (
          ad.apiRef.current.getAdContainerType?.() === AdContainerType.Parent
        ) {
          adsActions.addEventListener(AdEvents.AdBreakStarted, () =>
            adsActions.setIsAdLoading(true)
          );
          adsActions.addEventListener(AdEvents.AdComplete, () =>
            adsActions.setIsAdLoading(true)
          );
        }
      }
    });

    await Promise.all(loadPromises)
      .then(() => {
        console.debug('Ads successfully loaded');
        adsStore.isLoaded = true;

        eventListenersQueue.forEach(({ event, callback }) => {
          adsActions.addEventListener(event, callback);
        });

        adsActions.addEventListener(
          AdEvents.AdBreakStarted,
          updateIsInsideAd(true)
        );
        adsActions.addEventListener(
          AdEvents.AdBreakEnded,
          updateIsInsideAd(false)
        );
        adsActions.addEventListener(
          AdEvents.AdContentPlay,
          updateIsAdPlaying(true)
        );
        adsActions.addEventListener(
          AdEvents.AdContentPause,
          updateIsAdPlaying(false)
        );
        adsActions.addEventListener(
          AdEvents.AdCuepointsChanged,
          (data: AdEventDataType) => {
            data.cuepoints.forEach((cuepoint) => {
              if (
                !adsStore.markers.find(
                  (_marker) => _marker.start === cuepoint.start
                )
              ) {
                adsStore.markers.push(cuepoint);
              }
            });
          }
        );

        document.dispatchEvent(
          new AnalyticsTechSetAdContextsEvent({
            [AnalyticsEventParam.AdContextsInstance]:
              adsActions.getAdContexts(),
            ...createItemPayload(props.resource),
          })
        );
      })
      .catch((error: unknown) => {
        console.error('There was a problem during the loading of ads: ', error);
      });
  },
  reset() {
    if (adsStore.isLoaded) {
      adsStore.isLoaded = false;

      for (let index = eventListeners.length - 1; index >= 0; index--) {
        const { event, callback } = eventListeners[index] || {};

        if (event && callback) {
          adsActions.removeEventListener(event, callback);
        }
      }

      adsStore.adWrapper = null;
      adsStore.videoElement = null;
      adsStore.markers = [];
      adsStore.contentPausedOn = null;
      eventListenersQueue.splice(0, eventListenersQueue.length);

      // We need to use try block here to mitigate a problem that surfaces when user has
      // enabled AdBlock and tries to select a recommendation from the UpNext component
      // Without it whole app will crash
      // TODO: This can be removed once plugins are safe or SDK ensures that this situation won't ever happen
      try {
        adsStore.ads.forEach((ad) => {
          ad.apiRef.current?.reset();
        });
      } catch (ex) {
        console.error(
          'Resetting ads apiRef failed. Likely due to AdBlocker enabled'
        );
      }
    }
  },
};

export const useAdsStore = () => {
  return useSnapshot(adsStore) as AdsStore;
};

devtoolsApi.setStore('ads', adsStore, adsActions);
